import { Box, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { API_BASE_URL, authFetchJson } from '../../apiClient.ts';
import { Link } from 'react-router-dom';
import { formatReportDate } from '../../utils/helperUtils.ts';

interface EventReportRow {
  id: number;
  eventName: string;
  eventDate: string;
  location: string | null;
  invitedCount: number;
  confirmedCount: number;
  attendedCount: number;
  inPersonCount: number;
  zoomCount: number;
}

export const EventsReport: React.FC = () => {
  const [rows, setRows] = useState<EventReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetchJson(`${API_BASE_URL}/reports/events`)
      .then(({ json }) => {
        setRows(json);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load events report', error);
        setLoading(false);
      });
  }, []);

  const exportToCSV = () => {
    const headers = [
      'Event',
      'Date',
      'Location',
      'Invited',
      'Confirmed',
      'Attended',
      'In Person',
      'Zoom'
    ];
    const csvRows = rows.map((row) => [
      row.eventName,
      formatReportDate(row.eventDate),
      row.location ?? '',
      String(row.invitedCount),
      String(row.confirmedCount),
      String(row.attendedCount),
      String(row.inPersonCount),
      String(row.zoomCount)
    ]);

    const content = [headers, ...csvRows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `events-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'eventName',
        headerName: 'Event',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <MuiLink component={Link} to={`/group-events/${params.row.id}`} underline="hover">
            {params.value}
          </MuiLink>
        )
      },
      {
        field: 'eventDate',
        headerName: 'Date',
        width: 160,
        renderCell: (params) => formatReportDate(params.value)
      },
      { field: 'location', headerName: 'Location', width: 200 },
      { field: 'invitedCount', headerName: 'Invited', width: 120 },
      { field: 'confirmedCount', headerName: 'Confirmed', width: 130 },
      { field: 'attendedCount', headerName: 'Attended', width: 120 },
      { field: 'inPersonCount', headerName: 'In Person', width: 120 },
      { field: 'zoomCount', headerName: 'Zoom', width: 100 }
    ],
    []
  );

  return (
    <Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Box mb={2}>
            <Button variant="outlined" onClick={exportToCSV}>
              Download CSV
            </Button>
          </Box>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                sorting: {
                  sortModel: [{ field: 'eventDate', sort: 'desc' }]
                }
              }}
              density="standard"
              disableRowSelectionOnClick
            />
          </Box>
        </>
      )}
    </Box>
  );
};
